import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { paths } from '@Components/configs';

/*
 * This component is used to wrap all routes in the application.
 * It checks the authentication status and feature flags to determine
 * the appropriate access level for each route, and redirects users to
 * the correct pages based on their authentication state and the type of route.
 */
const AuthRouteWrapper = ({
  type, // 'product', 'admin', 'noauth'
  isAuthenticated = false,
  isAdminAuthenticated = false,
  flags = {},
  children,
}) => {
  const location = useLocation();

  // --- Product Auth Redirects ---
  if (type === 'product') {
    const restrictedPaths = [
      paths.productInfringements,
      paths.productResultsUpload,
      paths.productConfirmedRemovals,
      paths.productNewSearch,
    ];

    const isResultsOrSearchPage = location.pathname.includes('results')
      || location.pathname.includes('searches');

    if (
      isAuthenticated
      && !flags.searchEnabled
      && (restrictedPaths.includes(location.pathname) || isResultsOrSearchPage)
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (!flags.reviewEnabled && location.pathname === paths.productReview) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && !flags.reportsEnabled && location.pathname.includes('reports')) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && !flags.cyclopsEnabled && location.pathname.includes('cyclops')) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && flags.cyclopsEnabled && flags.cyclopsAnalyticsDisabled
      && location.pathname === paths.cyclopsAnalytics
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && flags.cyclopsEnabled && flags.cyclopsSettingsDisabled
      && location.pathname === paths.cyclopsSettings
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && location.pathname === paths.productPiracyOverview
      && !flags.overviewEnabled
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (!flags.allowSettings && (location.pathname.includes('settings')
      && location.pathname !== paths.cyclopsSettings
    )) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && !flags.contentIdEnabled
      && location.pathname.includes('content-id')
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && !flags.referralEnabled
      && location.pathname === paths.creatorReferrals
    ) {
      return <Navigate replace to={paths.root} />;
    }

    if (isAuthenticated && flags.unconfiguredCompany && location.pathname !== paths.account) {
      return <Navigate replace to={paths.account} />;
    }

    // Auth check
    if (!isAuthenticated) {
      return <Navigate replace to={paths.login} />;
    }
    return children;
  }

  // --- Admin Auth Redirects ---
  if (type === 'admin') {
    if (!isAdminAuthenticated) {
      return <Navigate replace to={paths.adminLogin} />;
    }
    return children;
  }

  // --- NoAuth Redirects ---
  if (type === 'noauth') {
    if (isAuthenticated || isAdminAuthenticated) {
      return <Navigate replace to={paths.root} />;
    }
    return children;
  }

  // Default fallback
  return <Navigate replace to={paths.root} />;
};

AuthRouteWrapper.propTypes = {
  type: PropTypes.oneOf(['product', 'admin', 'noauth']).isRequired,
  isAuthenticated: PropTypes.bool,
  isAdminAuthenticated: PropTypes.bool,
  flags: PropTypes.object,
  children: PropTypes.node.isRequired,
};

export default AuthRouteWrapper;
